import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FiBookOpen, FiCheckCircle, FiClock, FiTarget, FiTrendingUp } from 'react-icons/fi';
import { playSoundEffect } from '../lib/sfx';
import { useI18n } from '../i18n/I18nProvider';

const API = '/api';

const formatDuration = (seconds) => {
  const total = Math.max(0, Number(seconds || 0));
  const minutes = Math.floor(total / 60);
  const remainder = Math.floor(total % 60);
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
};

const formatToBeijingTime = (isoString) => {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return new Intl.DateTimeFormat('zh-CN', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(d);
  } catch (_) {
    return isoString;
  }
};

export default function ReviewAttemptPage() {
  const { t } = useI18n();
  const { resultId } = useParams();
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showIncorrectOnly, setShowIncorrectOnly] = useState(false);
  const [rescoring, setRescoring] = useState(new Set());
  const [pendingQuestionIdx, setPendingQuestionIdx] = useState(new Set());
  const [pendingVocabWords, setPendingVocabWords] = useState(new Set());
  const [savedQuestionIdx, setSavedQuestionIdx] = useState(new Set());
  const [savedVocabWords, setSavedVocabWords] = useState(new Set());
  const hasPlayedScoreRevealRef = useRef(false);
  const isAdminView = (() => {
    try { return new URLSearchParams(window.location.search).get('adminView') === '1'; } catch { return false; }
  })();

  useEffect(() => {
    const load = async () => {
      setIsLoading(true); setError(null);
      try {
        const r = await fetch(`${API}/results/${resultId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || 'Failed to fetch quiz result details.');
        setResult(j);
        // Auto-claim if result is anonymous so rescore can persist
        if (!j.username || j.username === '') {
          try {
            const cr = await fetch(`${API}/results/${resultId}/claim`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const cj = await cr.json().catch(() => ({}));
            if (cr.ok) setResult(cj);
          } catch {}
        }
      } catch (e) { setError(e.message || 'Failed to load'); }
      finally { setIsLoading(false); }
    };
    if (resultId) load();
  }, [resultId]);

  useEffect(() => {
    if (!result || hasPlayedScoreRevealRef.current) return;
    hasPlayedScoreRevealRef.current = true;
    const score = result?.score ?? 0;
    const total = result?.total_score ?? 0;
    const ratio = total > 0 ? score / total : 0;
    if (ratio >= 0.8) playSoundEffect('resultHigh', { volume: 0.9 });
    else if (ratio >= 0.5) playSoundEffect('resultMid', { volume: 0.78 });
    else playSoundEffect('resultLow', { volume: 0.7 });
  }, [result]);

  const filteredQuestions = useMemo(() => {
    if (!result?.details?.questions) return [];
    const list = result.details.questions.map((question, questionIndex) => ({
      question,
      questionIndex,
    }));
    if (!showIncorrectOnly) return list;
    return list.filter(({ question: q }) => {
      if (result?.details?.practice_type === 'adaptive_reading') return !q.correct;
      if (q.type === 'sentence') return typeof q.score === 'number' && q.score < 3;
      return q.type === 'fill-in-the-blank' ? !q.correct : false;
    });
  }, [result, showIncorrectOnly]);

  const recalcTotals = (updated) => {
    const qs = updated?.details?.questions || [];
    let s = 0, t = 0;
    qs.forEach(q => {
      if (q.type === 'fill-in-the-blank') { t += 1; if (q.correct) s += 1; }
      else if (q.type === 'sentence') { t += 4; s += Math.max(0, Math.min(4, typeof q.score === 'number' ? q.score : 0)); }
    });
    updated.score = s; updated.total_score = t; return updated;
  };

  const rescoreQuestion = async (index, q) => {
    playSoundEffect('rescore', { volume: 0.72 });
    const ns = new Set(rescoring); ns.add(index); setRescoring(ns);
    try {
      const token = localStorage.getItem('token');
      let endpoint, payload;
      if (q.type === 'fill-in-the-blank') {
        endpoint = `${API}/ai/fill-in-blank-score`;
        payload = { prompt: q.prompt || q.sentence, answer: q.answer, word: q.word };
      } else {
        endpoint = `${API}/ai/sentence-score`;
        payload = { word: q.word, sentence: q.answer, definition: q.definition };
      }
      const attempt = async () => {
        const r = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
        const text = await r.text();
        if (r.ok) { try { return JSON.parse(text); } catch { throw new Error('RETRY'); } }
        try { const err = JSON.parse(text); if (String(err?.error || '').includes('internal scoring error')) throw new Error('RETRY'); } catch {}
        throw new Error('RETRY');
      };
      let data;
      try { data = await attempt(); }
      catch { await new Promise(r => setTimeout(r, 300)); try { data = await attempt(); } catch { data = null; } }
      if (!data) throw new Error('Rescoring failed');

      const updated = { ...result };
      const qs = [...(updated?.details?.questions || [])];
      const nq = { ...qs[index] };
      let question_update = {};
      if (q.type === 'fill-in-the-blank') { nq.correct = !!data.correct; nq.feedback = data.feedback || nq.feedback || ''; question_update = { correct: nq.correct, feedback: nq.feedback }; }
      else { nq.score = typeof data.score === 'number' ? data.score : 0; nq.feedback = data.feedback || nq.feedback || ''; question_update = { score: nq.score, feedback: nq.feedback }; }
      qs[index] = nq;
      updated.details = { ...(updated.details || {}), questions: qs };
      setResult(recalcTotals(updated));

      // Persist to server
      try {
        const rid = result?.id || result?._id || resultId;
        let pr = await fetch(`${API}/results/${rid}/rescore`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ question_index: index, question_update }) });
        if (pr.status === 403) {
          // Attempt claim then retry once
          try {
            await fetch(`${API}/results/${rid}/claim`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
            pr = await fetch(`${API}/results/${rid}/rescore`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ question_index: index, question_update }) });
          } catch {}
        }
        if (pr.ok) {
          const saved = await pr.json();
          setResult(saved);
        }
      } catch {}
      playSoundEffect('refresh', { volume: 0.68 });
    } catch (e) {
      playSoundEffect('softFail', { volume: 0.72 });
      alert(e.message || 'Rescoring failed.');
    } finally {
      const ns2 = new Set(rescoring); ns2.delete(index); setRescoring(ns2);
    }
  };

  const handleToggleBookmarkQuestion = async (index, q) => {
    if (!result) return;
    const token = localStorage.getItem('token');
    try {
      const pend = new Set(pendingQuestionIdx); pend.add(index); setPendingQuestionIdx(pend);
      const rid = result._id || result.id || '';
      if (savedQuestionIdx.has(index)) {
        const r = await fetch(`${API}/user/bookmark-question`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ result_id: rid, question_index: index })
        });
        const j = await r.json().catch(()=>({}));
        if (!r.ok) throw new Error(j.error || 'Failed to unbookmark');
        const ns = new Set(savedQuestionIdx); ns.delete(index); setSavedQuestionIdx(ns);
        playSoundEffect('bookmarkOff', { volume: 0.62 });
      } else {
        const payload = {
          prompt: q.prompt || `Sentence for "${q.word || ''}"`,
          word: q.word || '',
          correct_answer: q.type === 'fill-in-the-blank' ? (q.word || '') : null,
          user_answer: q.answer || '',
          ai_feedback: q.feedback || null,
          quiz_id: result.quiz_id || null,
          result_id: rid || null,
          question_index: index,
        };
        const r = await fetch(`${API}/user/bookmark-question`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        const j = await r.json().catch(()=>({}));
        if (!r.ok) throw new Error(j.error || 'Failed to bookmark');
        const ns = new Set(savedQuestionIdx); ns.add(index); setSavedQuestionIdx(ns);
        playSoundEffect('bookmarkOn', { volume: 0.72 });
      }
    } catch (e) {
      playSoundEffect('softFail', { volume: 0.72 });
      alert(e.message || 'Bookmark action failed');
    } finally {
      const pend2 = new Set(pendingQuestionIdx); pend2.delete(index); setPendingQuestionIdx(pend2);
    }
  };

  const handleToggleVocab = async (q) => {
    const token = localStorage.getItem('token');
    try {
      const pend = new Set(pendingVocabWords); pend.add(q.word); setPendingVocabWords(pend);
      if (savedVocabWords.has(q.word)) {
        const r = await fetch(`${API}/user/save-vocab`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ word: q.word })
        });
        const j = await r.json().catch(()=>({}));
        if (!r.ok) throw new Error(j.error || 'Failed to unsave vocab');
        const ns = new Set(savedVocabWords); ns.delete(q.word); setSavedVocabWords(ns);
        playSoundEffect('bookmarkOff', { volume: 0.58 });
      } else {
        const r = await fetch(`${API}/user/save-vocab`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ word: q.word, definition: q.definition })
        });
        const j = await r.json().catch(()=>({}));
        if (!r.ok) throw new Error(j.error || 'Failed to save vocab');
        const ns = new Set(savedVocabWords); ns.add(q.word); setSavedVocabWords(ns);
        playSoundEffect(j?.message === 'already_exists' ? 'alreadySaved' : 'saveWord', { volume: 0.72 });
      }
    } catch (e) {
      playSoundEffect('softFail', { volume: 0.72 });
      alert(e.message || 'Vocab action failed');
    } finally {
      const pend2 = new Set(pendingVocabWords); pend2.delete(q.word); setPendingVocabWords(pend2);
    }
  };

  if (isLoading) return <p className="p-6 text-center">Loading review...</p>;
  if (error) return <p className="p-6 text-center text-red-500">Error: {error}</p>;
  if (!result) return <p className="p-6 text-center">No result found.</p>;

  const { details, score, total_score, ts } = result;
  const isAdaptiveReading = details?.practice_type === 'adaptive_reading';
  const reading = details?.reading || null;
  const analysis = details?.analysis || {};
  const percentage = Number(result?.percentage ?? (
    total_score > 0 ? (score / total_score) * 100 : 0
  ));

  return (
    <main className="p-4 md:p-8 max-w-5xl mx-auto">
      <header className="border-b border-gray-200 pb-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
          {t(isAdaptiveReading ? 'Reading result' : 'Quiz result')}
        </div>
        <h1 data-i18n-skip className="mt-1 text-3xl font-bold text-gray-900">{details?.name || 'Practice result'}</h1>
        <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 border border-gray-200 bg-white">
          <div className="p-4 border-b lg:border-b-0 lg:border-r border-gray-200">
            <div className="text-xs font-semibold uppercase text-gray-500">{t('Score')}</div>
            <div className="mt-1 text-2xl font-bold text-indigo-700">{score}/{total_score}</div>
          </div>
          <div className="p-4 border-b lg:border-b-0 lg:border-r border-gray-200">
            <div className="text-xs font-semibold uppercase text-gray-500">{t('Accuracy')}</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">{percentage.toFixed(1)}%</div>
          </div>
          <div className="p-4 border-r border-gray-200">
            <div className="text-xs font-semibold uppercase text-gray-500">{t('Time used')}</div>
            <div className="mt-1 flex items-center gap-2 text-xl font-bold text-gray-900"><FiClock /> {result.duration_seconds > 0 ? formatDuration(result.duration_seconds) : '—'}</div>
          </div>
          <div className="p-4">
            <div className="text-xs font-semibold uppercase text-gray-500">{t('Completed at')}</div>
            <div className="mt-1 text-sm font-semibold text-gray-900">{formatToBeijingTime(result.completed_at || ts)}</div>
          </div>
        </div>
      </header>

      {isAdaptiveReading && analysis?.summary && (
        <section className="py-7 border-b border-gray-200">
          <div className="flex items-center gap-2 text-teal-700"><FiTrendingUp /><h2 className="font-bold">{t('AI coaching review')}</h2></div>
          <p data-i18n-skip className="mt-3 text-lg leading-8 text-gray-800">{analysis.summary}</p>
          <div className="mt-5 grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-bold flex items-center gap-2"><FiCheckCircle className="text-emerald-600" /> {t('What went well')}</h3>
              <ul data-i18n-skip className="mt-2 list-disc pl-5 space-y-2 text-gray-700">{(analysis.strengths || []).map((item, index) => <li key={index}>{item}</li>)}</ul>
            </div>
            <div>
              <h3 className="font-bold flex items-center gap-2"><FiTarget className="text-indigo-600" /> {t('Next practice focus')}</h3>
              <ul data-i18n-skip className="mt-2 list-disc pl-5 space-y-2 text-gray-700">{(analysis.next_steps || []).map((item, index) => <li key={index}>{item}</li>)}</ul>
            </div>
          </div>
          {analysis.estimated_performance && <p data-i18n-skip className="mt-5 border-l-4 border-indigo-400 bg-indigo-50 px-4 py-3 text-indigo-900">{analysis.estimated_performance}</p>}
        </section>
      )}

      {isAdaptiveReading && reading?.paragraphs?.length > 0 && (
        <details className="py-6 border-b border-gray-200">
          <summary className="cursor-pointer inline-flex items-center gap-2 font-bold text-lg text-gray-900"><FiBookOpen className="text-teal-600" /> {t('Read the passage again')}</summary>
          <div data-i18n-skip className="mt-5 space-y-5 text-[17px] leading-8 text-gray-800">
            {reading.paragraphs.map((paragraph) => <p key={paragraph.id}><strong className="mr-2 text-gray-500">{paragraph.id}</strong>{paragraph.text}</p>)}
          </div>
        </details>
      )}

      <div className="flex items-center justify-between gap-4 mt-7 mb-4">
        <h2 className="text-xl font-bold text-gray-900">{t('Question review')}</h2>
        <button
          onClick={() => setShowIncorrectOnly(v => !v)}
          className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700"
        >
          {t(showIncorrectOnly ? 'Show All' : 'Show Incorrect Only')}
        </button>
      </div>

      <div className="space-y-4">
        {filteredQuestions.map(({ question: q, questionIndex }) => {
          const isSentenceCorrect = q.type === 'sentence' && typeof q.score === 'number' && q.score >= 3;
          const isCorrect = isAdaptiveReading
            ? !!q.correct
            : q.type === 'fill-in-the-blank'
              ? !!q.correct
              : isSentenceCorrect;
          const points = isAdaptiveReading
            ? (q.correct ? 1 : 0)
            : q.type === 'fill-in-the-blank'
              ? (q.correct ? 1 : 0)
              : (typeof q.score === 'number' ? q.score : 0);
          const prompt = (q.prompt || q.sentence || '').replace('___', `[${q.word}]`) || `Sentence for "${q.word}"`;
          return (
            <div key={q.id || questionIndex} className={`border-l-4 p-4 rounded-md ${isCorrect ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
              <div className="flex justify-between items-start">
                <p data-i18n-skip className="text-lg font-semibold text-gray-700"><span className="mr-2">{questionIndex + 1}.</span>{prompt}</p>
                <span className={`font-bold ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                  {points}/{isAdaptiveReading || q.type !== 'sentence' ? 1 : 4}
                </span>
              </div>
              <p className="mt-2">{t('Your answer')}: <span data-i18n-skip className="font-mono p-1 bg-gray-200 rounded text-gray-800">{(isAdaptiveReading ? q.response : q.answer) || t('No answer')}</span></p>

              {isAdaptiveReading && (
                <div className="mt-3 space-y-2 text-sm text-gray-700">
                  <p><strong>{t('Correct answer')}:</strong> <span data-i18n-skip>{q.answer}</span></p>
                  {q.evidence && <p><strong>{t('Evidence')}:</strong> <span data-i18n-skip>{q.evidence}</span></p>}
                  {q.explanation && <p><strong>{t('Why')}:</strong> <span data-i18n-skip>{q.explanation}</span></p>}
                </div>
              )}

              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {!isAdaptiveReading && (
                  <button
                    onClick={() => rescoreQuestion(questionIndex, q)}
                    disabled={rescoring.has(questionIndex)}
                    className={`inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${rescoring.has(questionIndex) ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`}
                  >
                    {t(rescoring.has(questionIndex) ? 'Rescoring...' : 'Rescore')}
                  </button>
                )}
                {!isAdminView && !isAdaptiveReading && (
                  <>
                    <button
                      onClick={() => handleToggleBookmarkQuestion(questionIndex, q)}
                      disabled={pendingQuestionIdx.has(questionIndex)}
                      className={`inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${pendingQuestionIdx.has(questionIndex) ? 'bg-gray-400 cursor-not-allowed' : (savedQuestionIdx.has(questionIndex) ? 'bg-rose-500 hover:bg-rose-600' : 'bg-amber-600 hover:bg-amber-700')}`}
                    >
                      {t(pendingQuestionIdx.has(questionIndex) ? 'Working...' : (savedQuestionIdx.has(questionIndex) ? 'Unbookmark' : 'Bookmark'))}
                    </button>
                    {q.word && (
                      <button
                        onClick={() => handleToggleVocab(q)}
                        disabled={pendingVocabWords.has(q.word)}
                        className={`inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${pendingVocabWords.has(q.word) ? 'bg-gray-400 cursor-not-allowed' : (savedVocabWords.has(q.word) ? 'bg-fuchsia-600 hover:bg-fuchsia-700' : 'bg-emerald-600 hover:bg-emerald-700')}`}
                      >
                        {t(pendingVocabWords.has(q.word) ? 'Working...' : (savedVocabWords.has(q.word) ? 'Unsave Vocab' : 'Save Vocab'))}
                      </button>
                    )}
                  </>
                )}
              </div>

              {q.feedback && (
                <div className="mt-2 text-sm text-blue-700 p-2 bg-blue-100 rounded">
                  <strong>{t('AI Feedback')}:</strong> <span data-i18n-skip>{q.feedback}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </main>
  );
}
